import { cloneDocumentMeta, createTextLayer } from '../core/document';
import { findLayerById, flattenLayers, insertLayerAt, mapLayer } from '../core/layerTree';
import type { DocumentMeta, RasterLayer, TextLayer, RGBA } from '../core/types';
import { getEngine } from '../engine/engine';
import { metaOp, surfaceOp } from '../engine/historyOps';
import { hitTestTextLayer, renderTextLayer } from '../engine/textOps';
import { activeDocument, commitHistory, commitMeta, insertAboveActive } from './actions';
import { beginTextEditSession, endTextEditSession, getTextEditSession } from './textEdit';
import { useAppStore } from './store';

const engine = getEngine();

function state() {
  return useAppStore.getState();
}

function findText(doc: DocumentMeta, layerId: string): TextLayer | null {
  const layer = findLayerById(doc.layers, layerId);
  return layer && layer.kind === 'text' ? layer : null;
}

/** Topmost visible text layer under the pointer. */
export function topTextLayerAt(point: { x: number; y: number }): TextLayer | null {
  const doc = activeDocument();
  if (!doc) return null;
  const flat = flattenLayers(doc.layers);
  for (let i = flat.length - 1; i >= 0; i -= 1) {
    const layer = flat[i]!;
    if (layer.kind === 'text' && layer.visible && hitTestTextLayer(layer, point)) {
      return layer;
    }
  }
  return null;
}

/** Creates a text layer at the click point and opens the in-canvas editor. */
export function placeTextLayer(point: { x: number; y: number }): string | null {
  const doc = activeDocument();
  if (!doc) return null;
  const metaBefore = cloneDocumentMeta(doc);
  const options = state().toolOptions;
  const layer = createTextLayer('Text', {
    content: '',
    x: point.x,
    y: point.y,
    fontFamily: options.textFontFamily,
    fontSize: options.textFontSize,
    fontWeight: options.textBold ? 'bold' : 'normal',
    fontStyle: options.textItalic ? 'italic' : 'normal',
    align: options.textAlign,
    color: { ...state().foreground },
  });
  const layers = insertLayerAt(doc.layers, insertAboveActive(doc), layer);
  const next: DocumentMeta = { ...doc, layers, activeLayerId: layer.id };
  state().setDocument(next);
  renderTextLayer(engine, next, layer, false);
  beginTextEditSession(layer.id, true, next.id, metaBefore, null);
  return layer.id;
}

export function startEditingTextLayer(layerId: string): void {
  const doc = activeDocument();
  if (!doc || !findText(doc, layerId)) return;
  const next = { ...doc, activeLayerId: layerId };
  state().setDocument(next);
  beginTextEditSession(
    layerId,
    false,
    next.id,
    cloneDocumentMeta(doc),
    engine.snapshotSurface(doc, layerId),
  );
}

export function updateTextTransient(patch: Partial<TextLayer>): void {
  const session = getTextEditSession();
  const doc = activeDocument();
  if (!session || !doc) return;
  const text = findText(doc, session.layerId);
  if (!text) return;
  const updated: TextLayer = { ...text, ...patch };
  state().replaceDocument(doc.id, (current) => ({
    ...current,
    layers: mapLayer(current.layers, session.layerId, () => updated),
  }));
  renderTextLayer(engine, doc, updated, false);
  engine.invalidate();
}

/** Commits the open edit session (or cancels an empty new layer). */
export function commitTextEdit(): void {
  const session = getTextEditSession();
  const doc = activeDocument();
  if (!session || !doc || doc.id !== session.docId) {
    endTextEditSession();
    return;
  }
  const text = findText(doc, session.layerId);
  endTextEditSession();
  if (!text) return;

  if (session.isNew && text.content.trim().length === 0) {
    // Drop the uncommitted empty layer without a history entry.
    state().setDocument(session.metaBefore);
    engine.peek(doc.id)?.deleteLayer(session.layerId);
    engine.invalidate();
    return;
  }

  commitHistory(doc.id, session.isNew ? 'New Text Layer' : 'Edit Text', [
    surfaceOp(text.id, session.surfaceBefore, engine.snapshotSurface(doc, text.id)),
    metaOp(session.metaBefore, cloneDocumentMeta(doc)),
  ]);
  state().replaceDocument(doc.id, (current) =>
    current.dirty ? current : { ...current, dirty: true },
  );
}

export function setTextLayerProps(
  layerId: string,
  patch: Partial<TextLayer>,
  label = 'Edit Text',
): void {
  const doc = activeDocument();
  if (!doc) return;
  const text = findText(doc, layerId);
  if (!text) return;
  const updated: TextLayer = { ...text, ...patch };
  const ops = renderTextLayer(engine, doc, updated);
  commitMeta(doc, label, { ...doc, layers: mapLayer(doc.layers, layerId, () => updated) }, ops);
  if (
    patch.fontFamily !== undefined ||
    patch.fontSize !== undefined ||
    patch.align !== undefined ||
    patch.fontWeight !== undefined ||
    patch.fontStyle !== undefined
  ) {
    state().patchToolOptions({
      ...(patch.fontFamily !== undefined ? { textFontFamily: patch.fontFamily } : {}),
      ...(patch.fontSize !== undefined ? { textFontSize: patch.fontSize } : {}),
      ...(patch.align !== undefined ? { textAlign: patch.align } : {}),
      ...(patch.fontWeight !== undefined ? { textBold: patch.fontWeight === 'bold' } : {}),
      ...(patch.fontStyle !== undefined ? { textItalic: patch.fontStyle === 'italic' } : {}),
    });
  }
}

function toRasterLayer(text: TextLayer): RasterLayer {
  return {
    kind: 'raster',
    id: text.id,
    name: text.name,
    visible: text.visible,
    opacity: text.opacity,
    blendMode: text.blendMode,
    blendChannels: text.blendChannels,
    blendIf: text.blendIf,
    locked: text.locked,
    lockPixels: text.lockPixels,
    lockPosition: text.lockPosition,
    lockMask: text.lockMask,
    hasMask: text.hasMask,
    maskEnabled: text.maskEnabled,
    maskLinked: text.maskLinked,
    clipping: text.clipping,
    vectorMask: text.vectorMask,
    effects: text.effects,
    contentOffset: text.contentOffset ?? { x: 0, y: 0 },
  };
}

/** Layer > Rasterize Type — keeps pixels, drops editable text fields. */
export function rasterizeTextLayer(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const text = findText(doc, layerId);
  if (!text) return;
  if (getTextEditSession()?.layerId === layerId) commitTextEdit();
  const next: DocumentMeta = {
    ...doc,
    layers: mapLayer(doc.layers, layerId, (layer) =>
      layer.kind === 'text' ? toRasterLayer(layer) : layer,
    ),
  };
  commitMeta(doc, 'Rasterize Type', next);
}

export function applyForegroundToText(layerId: string, color: RGBA): void {
  setTextLayerProps(layerId, { color }, 'Text Color');
}

/** Move text as editable geometry so it stays sharp (like shape layers). */
export function moveTextLayer(layerId: string, dx: number, dy: number, label = 'Move Text'): boolean {
  const doc = activeDocument();
  if (!doc) return false;
  const text = findText(doc, layerId);
  if (!text || (dx === 0 && dy === 0)) return false;
  setTextLayerProps(layerId, { x: text.x + dx, y: text.y + dy }, label);
  return true;
}

/** Opens the Type tool and the in-canvas editor for a text layer. */
export function editTextLayer(layerId: string): void {
  const doc = activeDocument();
  if (!doc || !findText(doc, layerId)) return;
  state().setTool('type');
  startEditingTextLayer(layerId);
}
